import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth } from './FirebaseConfig';
import { getFirestore, doc, getDoc, updateDoc, arrayUnion } from 'firebase/firestore';

const UserContext = createContext();

export function UserProvider({ children }) {
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null); // Store subscription data
  const [favorites, setFavorites] = useState([]); // Store user's favorite places
  const db = getFirestore();

  const login = (userData) => {
    setUser(userData);
    fetchUserData(userData.uid); // Fetch Firestore data after login
  };

  const logout = () => {
    setUser(null);
    setSubscription(null);
    setFavorites([]);
    auth.signOut();
  };

  // Fetch user data from Firestore
  const fetchUserData = async (uid) => {
    try {
      const userRef = doc(db, 'Users', uid);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        const data = userSnap.data();
        setSubscription(data.subscription || '0'); // Default to "0" if not set
        setFavorites(data.favorites || []); // Default to empty array if not set
      } else {
        console.log('No user document found in Firestore.');
        setSubscription('0'); // Default to free plan for new users
        setFavorites([]); // Default to empty favorites
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  };

  // Add a favorite place
  const addFavorite = async (placeId) => {
    if (!user) {
      console.error('No user logged in to add favorite.');
      return;
    }

    try {
      const userRef = doc(db, 'Users', user.uid);
      await updateDoc(userRef, {
        favorites: arrayUnion(placeId), // Add placeId to the "favorites" array
      });
      setFavorites((prev) => [...prev, placeId]); // Update local state
      console.log('Favorite added successfully:', placeId);
    } catch (error) {
      console.error('Error adding favorite:', error);
    }
  };

  // Monitor Firebase auth state and fetch Firestore data
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        fetchUserData(firebaseUser.uid); // Fetch Firestore data when user logs in
      } else {
        setUser(null);
        setSubscription(null);
        setFavorites([]);
      }
    });
    return unsubscribe;
  }, [db]);

  return (
    <UserContext.Provider
      value={{
        user,
        subscription,
        favorites,
        login,
        logout,
        addFavorite,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

export const useUser = () => useContext(UserContext);
